import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PortalStatus from '@/components/portal/PortalStatus';
import Panel from '@/components/ui/Panel';
import { shortDate } from '@/lib/format';
import { contractPdfUrl, onboardingApi, type Contract } from '@/lib/onboarding';

/** Портал партнёра: действующий договор на реальных данных (этап 1.2б) — условия из шаблона, PDF, подпись. */
export default function PortalContractPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<{ contract: Contract | null; html?: string } | null>(null);

  useEffect(() => {
    onboardingApi.myContract().then(setData).catch(() => setData({ contract: null }));
  }, []);

  const c = data?.contract ?? null;
  const statusMap: Record<string, { status: 'Confirmed' | 'Requested' | 'Rejected'; label: string }> = {
    Signed: { status: 'Confirmed', label: t('portal.contract.statusActive') },
    Sent: { status: 'Requested', label: t('onb.ctrStatus.Sent') },
    Draft: { status: 'Requested', label: t('onb.ctrStatus.Draft') },
    Expired: { status: 'Rejected', label: t('onb.ctrStatus.Expired') },
  };
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 border-b border-background-200 pb-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-[20px] font-semibold leading-6 text-foreground-950">{t('portal.contract.title')}</h1>
          <p className="mt-1 max-w-3xl text-[12.5px] leading-5 text-foreground-500">{t('portal.contract.subtitle')}</p>
        </div>
        {c && (
          <div className="flex flex-wrap items-center gap-2">
            <PortalStatus status={statusMap[c.status]?.status ?? 'Requested'} label={statusMap[c.status]?.label ?? c.status} />
            <a href={contractPdfUrl(c.id, true)} target="_blank" rel="noreferrer" className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600">
              <i className="ri-download-2-line text-[13px]" />
              {t('portalContract.pdf')}
            </a>
          </div>
        )}
      </div>

      {data && !c && (
        <Panel>
          <p className="text-[13px] text-foreground-700">{t('portalContract.none')}</p>
        </Panel>
      )}

      {c && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="xl:col-span-2">
            <Panel padded={false}>
              <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
                <div>
                  <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.contract.preview')}</h2>
                  <p className="mt-0.5 text-[11px] text-foreground-500">{c.number} · v{c.version}</p>
                </div>
                <i className="ri-file-pdf-2-line text-[17px] text-foreground-300" />
              </div>
              <div className="bg-background-200/60 px-3 py-4 sm:px-6 sm:py-6">
                <div className="mx-auto w-full max-w-[680px] bg-background-50 px-6 py-7 text-[12.5px] leading-6 text-foreground-800 shadow-sm [&_h1]:mb-3 [&_h1]:text-[16px] [&_h1]:font-bold [&_h2]:mb-1 [&_h2]:mt-4 [&_h2]:text-[13px] [&_h2]:font-semibold [&_p]:my-1">
                  <div dangerouslySetInnerHTML={{ __html: data?.html || '' }} />
                </div>
              </div>
            </Panel>
          </div>
          <div className="flex flex-col gap-4">
            <Panel>
              <h2 className="text-[13px] font-semibold text-foreground-950">{t('onb.ctrTerms')}</h2>
              <dl className="mt-3 space-y-2 text-[12px]">
                <div className="flex justify-between gap-3"><dt className="text-foreground-500">{t('onb.ctrStart')}</dt><dd className="font-medium text-foreground-900">{c.startDate ? shortDate(c.startDate) : '—'}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-foreground-500">{t('onb.ctrEnd')}</dt><dd className="font-medium text-foreground-900">{c.endDate ? shortDate(c.endDate) : '—'}</dd></div>
                {Object.entries(c.terms).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3"><dt className="text-foreground-500">{k.replace(/_/g, ' ')}</dt><dd className="tnum font-medium text-foreground-900">{String(v ?? '—') || '—'}</dd></div>
                ))}
              </dl>
              {c.signedByName && <p className="mt-3 border-t border-background-200 pt-3 text-[11.5px] text-foreground-500">{t('portalContract.signedBy', { name: c.signedByName, date: fmt(c.signedAt) })}</p>}
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
